import { Head, Link, usePage } from '@inertiajs/react';

import { trackUmamiEvent } from '@/lib/analytics';
import { parishShortName, useSite } from '@/hooks/useSite';

interface DashboardProps {
  counts: { news: number; events: number; documents: number; images: number };
  adminStats?: { pendingMessages: number; pendingRequests: number };
  [key: string]: unknown;
}

const QUICK_ACTIONS = [
  { to: '/news', label: 'Gerir notícias', icon: 'campaign' },
  { to: '/events', label: 'Gerir eventos', icon: 'event' },
  { to: '/documents', label: 'Carregar documento', icon: 'description' },
  { to: '/images', label: 'Carregar imagem', icon: 'image' },
] as const;

const sectionLabel = 'text-on-surface-light/70 text-xs font-bold tracking-widest uppercase';

export default function Dashboard() {
  const { counts, adminStats } = usePage<DashboardProps>().props;
  const site = useSite();
  const pendingMessages = adminStats?.pendingMessages ?? 0;
  const pendingRequests = adminStats?.pendingRequests ?? 0;
  const hasAttention = pendingMessages > 0 || pendingRequests > 0;

  const today = new Date().toLocaleDateString('pt-PT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const content = [
    { to: '/news', label: 'Notícias', icon: 'campaign', n: counts.news },
    { to: '/events', label: 'Eventos', icon: 'event', n: counts.events },
    { to: '/documents', label: 'Documentos', icon: 'description', n: counts.documents },
    { to: '/images', label: 'Imagens', icon: 'image', n: counts.images },
  ];

  return (
    <>
      <Head title="Início — Administração" />

      {!site.published && (
        <Link
          href="/onboarding"
          className="border-secondary/40 bg-secondary/[0.08] hover:border-secondary mb-6 flex items-center gap-4 rounded-lg border p-5 transition-colors"
        >
          <span className="bg-secondary/15 text-secondary flex h-11 w-11 shrink-0 items-center justify-center rounded-lg">
            <span className="material-symbols-outlined">settings</span>
          </span>
          <span>
            <span className="text-on-surface block text-sm font-semibold">
              O seu site ainda não está publicado
            </span>
            <span className="text-on-surface-light block text-sm">
              Conclua a configuração e publique o site para o tornar visível ao público.
            </span>
          </span>
          <span className="material-symbols-outlined text-secondary ml-auto">arrow_forward</span>
        </Link>
      )}

      <div className="space-y-1">
        <p className={sectionLabel}>Início</p>
        <h1 className="font-headline text-on-surface text-3xl font-semibold">Bem-vindo de volta</h1>
        <p className="text-on-surface-light text-sm">
          Resumo da atividade da {parishShortName(site)} <span aria-hidden="true">·</span>{' '}
          {today.charAt(0).toUpperCase() + today.slice(1)}
        </p>
      </div>

      {/* Needs attention */}
      <p className={`${sectionLabel} mt-8 mb-3`}>Precisa de atenção</p>
      {hasAttention ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {pendingMessages > 0 && (
            <Link
              href="/messages"
              onClick={() =>
                trackUmamiEvent('admin_dashboard_action', {
                  section: 'needs_attention',
                  label: 'Mensagens pendentes',
                  target: '/messages',
                })
              }
              className="border-line bg-surface hover:border-primary group flex items-center gap-4 rounded-lg border p-5 transition-colors"
            >
              <span className="bg-primary/10 text-primary flex h-11 w-11 shrink-0 items-center justify-center rounded-lg">
                <span className="material-symbols-outlined">mail</span>
              </span>
              <span>
                <span className="font-headline text-on-surface block text-3xl leading-none font-semibold">
                  {pendingMessages}
                </span>
                <span className="text-on-surface-light text-sm">Mensagens pendentes</span>
              </span>
              <span className="material-symbols-outlined text-on-surface-light group-hover:text-primary ml-auto">
                arrow_forward
              </span>
            </Link>
          )}
          {pendingRequests > 0 && (
            <Link
              href="/requests"
              onClick={() =>
                trackUmamiEvent('admin_dashboard_action', {
                  section: 'needs_attention',
                  label: 'Pedidos pendentes',
                  target: '/requests',
                })
              }
              className="border-line bg-surface hover:border-primary group flex items-center gap-4 rounded-lg border p-5 transition-colors"
            >
              <span className="bg-secondary/10 text-secondary flex h-11 w-11 shrink-0 items-center justify-center rounded-lg">
                <span className="material-symbols-outlined">inbox</span>
              </span>
              <span>
                <span className="font-headline text-on-surface block text-3xl leading-none font-semibold">
                  {pendingRequests}
                </span>
                <span className="text-on-surface-light text-sm">Pedidos pendentes</span>
              </span>
              <span className="material-symbols-outlined text-on-surface-light group-hover:text-primary ml-auto">
                arrow_forward
              </span>
            </Link>
          )}
        </div>
      ) : (
        <div className="border-tertiary/30 bg-tertiary/[0.06] flex items-center gap-3 rounded-lg border p-5">
          <span className="text-tertiary material-symbols-outlined">check_circle</span>
          <p className="text-on-surface text-sm">
            Tudo em dia. Não há mensagens nem pedidos pendentes.
          </p>
        </div>
      )}

      {/* Quick actions */}
      <p className={`${sectionLabel} mt-8 mb-3`}>Ações rápidas</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {QUICK_ACTIONS.map(({ to, label, icon }) => (
          <Link
            key={label}
            href={to}
            onClick={() =>
              trackUmamiEvent('admin_dashboard_action', {
                section: 'quick_actions',
                label,
                target: to,
              })
            }
            className="border-line bg-surface hover:border-primary flex flex-col items-center gap-2 rounded-lg border p-5 text-center transition-colors"
          >
            <span className="material-symbols-outlined text-primary text-[26px]">{icon}</span>
            <span className="text-on-surface text-sm font-semibold">{label}</span>
          </Link>
        ))}
      </div>

      {/* Published content overview */}
      <p className={`${sectionLabel} mt-8 mb-3`}>Conteúdo registado</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {content.map(({ to, label, icon, n }) => (
          <Link
            key={label}
            href={to}
            onClick={() =>
              trackUmamiEvent('admin_dashboard_action', {
                section: 'content_overview',
                label,
                target: to,
              })
            }
            className="border-line bg-surface hover:border-primary rounded-lg border p-4 transition-colors"
          >
            <span className="font-headline text-on-surface block text-2xl font-semibold">{n}</span>
            <span className="text-on-surface-light mt-0.5 flex items-center gap-1.5 text-sm">
              <span className="material-symbols-outlined text-on-surface-light/70 text-[15px]">
                {icon}
              </span>
              {label}
            </span>
          </Link>
        ))}
      </div>
    </>
  );
}
